import React, { useEffect } from 'react';
import { motion, useAnimation } from 'framer-motion';
import { useInView } from 'react-intersection-observer';
import 'bulma/css/bulma.min.css';
import { InfoService } from '../../../services/site/InfoService';
import VideoPlayer from '../../../Components/VideoPlayer';
import onboardingVideo from "../../../assets/videos/vendor.mp4";

// Animation variants
const fadeInUp = {
  hidden: { opacity: 0, y: 60 },
  visible: { opacity: 1, y: 0, transition: { duration: 0.6 } }
};

const fadeInLeft = {
  hidden: { opacity: 0, x: -60 },
  visible: { opacity: 1, x: 0, transition: { duration: 0.6 } }
};

const fadeInRight = {
  hidden: { opacity: 0, x: 60 },
  visible: { opacity: 1, x: 0, transition: { duration: 0.6 } }
};

const staggerContainer = {
  hidden: { opacity: 0 },
  visible: {
    opacity: 1,
    transition: { staggerChildren: 0.15 }
  }
};

const scaleIn = {
  hidden: { scale: 0.8, opacity: 0 },
  visible: { scale: 1, opacity: 1, transition: { duration: 0.5 } }
};

// Animated section wrapper
const AnimatedSection: React.FC<{
  children: React.ReactNode;
  className?: string;
  variant?: any;
}> = ({ children, className = '', variant = fadeInUp }) => {
  const controls = useAnimation();
  const [ref, inView] = useInView({ threshold: 0.1, triggerOnce: true });

  useEffect(() => {
    if (inView) {
      controls.start('visible');
    }
  }, [controls, inView]);

  return (
    <motion.section
      ref={ref}
      initial="hidden"
      animate={controls}
      variants={variant}
      className={className}
    >
      {children}
    </motion.section>
  );
};

// Hero Section
const HeroSection: React.FC = () => {
  return (
    <AnimatedSection className="sds_vo-hero section">
      <div className="container">
        <div className="columns is-vcentered">
          <div className="column is-6">
            <motion.div
              initial={{ opacity: 0, x: -50 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.8 }}
            >
              <h1 className="title is-1 sds_vo-hero-title">
                Vendor Onboarding System
              </h1>
              <h2 className="subtitle is-4 sds_vo-hero-subtitle mb-5">
                Streamline Vendor Registration with AI & Digital Signatures
              </h2>
              <div className="content is-size-5 sds_vo-hero-description">
                <p className="mb-4">
                  <strong>Smart Vendor Management:</strong> Our comprehensive vendor onboarding platform
                  automates document verification, compliance checks, and contract signing to help you
                  build a reliable supplier network quickly and securely.
                </p>
                <p className="mb-4">
                  Register vendors with ease - collect documents, verify credentials, extract data with AI,
                  and execute contracts digitally. Complete vendor onboarding in hours, not weeks!
                </p>
              </div>
              <div className="sds_vo-feature-badges">
                <span className="tag is-large sds_vo-badge">🏢 Smart Registration</span>
                <span className="tag is-large sds_vo-badge">🤖 AI Verification</span>
                <span className="tag is-large sds_vo-badge">✍️ E-Contracts</span>
                <span className="tag is-large sds_vo-badge">🔒 Compliant</span>
              </div>
            </motion.div>
          </div>
          <div className="column is-6">
            <motion.div
              initial={{ opacity: 0, scale: 0.8 }}
              animate={{ opacity: 1, scale: 1 }}
              transition={{ duration: 0.8, delay: 0.3 }}
              className="sds_vo-hero-visual"
            >
              <div className="sds_vo-animation">
                <motion.div
                  className="sds_vo-icon-large"
                  animate={{
                    rotate: [0, -5, 5, -5, 0],
                    scale: [1, 1.05, 1]
                  }}
                  transition={{ duration: 3, repeat: Infinity, repeatDelay: 2 }}
                >
                  🏢
                </motion.div>
                <div className="sds_vo-floating-elements">
                  {['📋', '✅', '🔐', '📊'].map((icon, i) => (
                    <motion.div
                      key={i}
                      className="sds_vo-floating-element"
                      animate={{ y: [-10, 10, -10] }}
                      transition={{ duration: 2 + i, repeat: Infinity, ease: "easeInOut" }}
                    >
                      {icon}
                    </motion.div>
                  ))}
                </div>
              </div>
            </motion.div>
          </div>
        </div>

        {/* Featured Video - Vendor Onboarding Demo */}
        <VideoPlayer
          videoSrc={onboardingVideo}
          title="Vendor Onboarding Simplified"
          description="Learn how to onboard vendors efficiently with streamlined data extraction, compliance document collection, and legally valid digital signatures"
          lightBackground={false}
        />
      </div>
    </AnimatedSection>
  );
};

// How It Works Section
const HowItWorksSection: React.FC = () => {
  const steps = [
    {
      number: 1,
      icon: '📝',
      title: 'Vendor Registration Portal',
      desc: 'Vendors fill out comprehensive registration form with business details, contact information, and service offerings.',
      features: ['Self-service portal', 'Progressive forms', 'Auto-save', 'Multi-language support']
    },
    {
      number: 2,
      icon: '📄',
      title: 'Document Upload',
      desc: 'Vendors upload required documents - business licenses, tax certificates, insurance, bank details, and certifications.',
      features: ['Document checklist', 'Multiple formats', 'Secure upload', 'Bulk upload']
    },
    {
      number: 3,
      icon: '🔍',
      title: 'Document Verification',
      desc: 'System automatically verifies document authenticity, checks expiry dates, and validates registration numbers.',
      features: ['Auto-verification', 'Expiry tracking', 'Authenticity check', 'Red flag alerts']
    },
    {
      number: 4,
      icon: '🤖',
      title: 'AI Data Extraction',
      desc: 'AI extracts key information from documents - business details, tax IDs, bank info, certificates, and compliance data.',
      features: ['Smart extraction', 'Multi-doc processing', '99% accuracy', 'Instant results']
    },
    {
      number: 5,
      icon: '✅',
      title: 'Compliance Checks',
      desc: 'Automated compliance verification against regulatory requirements, industry standards, and internal policies.',
      features: ['Regulatory checks', 'Risk assessment', 'Compliance scoring', 'Approval workflow']
    },
    {
      number: 6,
      icon: '📋',
      title: 'Contract Generation',
      desc: 'System auto-populates vendor agreements, purchase orders, and NDAs with extracted data from templates.',
      features: ['Template library', 'Custom contracts', 'Smart fields', 'Preview & edit']
    },
    {
      number: 7,
      icon: '✍️',
      title: 'Digital Signing',
      desc: 'Both parties sign contracts digitally with legally binding e-signatures - instant, secure, and compliant.',
      features: ['E-signature flow', 'Multi-party signing', 'Reminders', 'Audit trail']
    },
    {
      number: 8,
      icon: '💾',
      title: 'Vendor Database',
      desc: 'Complete vendor profile with all documents, contracts, and data automatically stored in centralized database.',
      features: ['Central repository', 'Easy search', 'Export options', 'API access']
    }
  ];

  return (
    <AnimatedSection className="sds_vo-process section sds_vo-bg-light">
      <div className="container">
        <div className="has-text-centered mb-6">
          <h2 className="title is-2 sds_vo-section-title">Complete Vendor Onboarding in 8 Steps</h2>
          <p className="subtitle is-5">From registration to approved vendor</p>
        </div>

        <motion.div
          className="columns is-multiline"
          variants={staggerContainer}
          initial="hidden"
          whileInView="visible"
          viewport={{ once: true }}
        >
          {steps.map((step, idx) => (
            <motion.div key={idx} className="column is-3" variants={fadeInUp}>
              <motion.div
                className="sds_vo-process-card"
                whileHover={{ y: -10 }}
                transition={{ type: "spring", stiffness: 300 }}
              >
                <div className="sds_vo-step-number">{step.number}</div>
                <div className="sds_vo-step-icon">{step.icon}</div>
                <h4 className="title is-6">{step.title}</h4>
                <p className="is-size-7 has-text-grey mb-3">{step.desc}</p>
                <ul className="sds_vo-feature-list">
                  {step.features.map((feature, fIdx) => (
                    <li key={fIdx}>
                      <span className="icon has-text-success is-small">
                        <i className="fas fa-check"></i>
                      </span>
                      <span>{feature}</span>
                    </li>
                  ))}
                </ul>
              </motion.div>
            </motion.div>
          ))}
        </motion.div>

      </div>
    </AnimatedSection>
  );
};

// Key Features Section
const KeyFeaturesSection: React.FC = () => {
  const features = [
    {
      icon: '🔍',
      title: 'Document Verification',
      desc: 'Automated validation of business licenses, certificates, and compliance documents.',
      color: '#4CAF50'
    },
    {
      icon: '🤖',
      title: 'AI Data Extraction',
      desc: 'Intelligent extraction of vendor information from uploaded documents.',
      color: '#2196F3'
    },
    {
      icon: '✅',
      title: 'Compliance Engine',
      desc: 'Automated compliance checks against regulatory and internal requirements.',
      color: '#FF9800'
    },
    {
      icon: '📋',
      title: 'Contract Management',
      desc: 'Template-based contract generation and lifecycle management.',
      color: '#9C27B0'
    },
    {
      icon: '✍️',
      title: 'E-Signature Integration',
      desc: 'Fully integrated digital signature workflows for all contracts.',
      color: '#F44336'
    },
    {
      icon: '📊',
      title: 'Vendor Portal',
      desc: 'Self-service portal for vendors to update info and track status.',
      color: '#00BCD4'
    },
    {
      icon: '🔒',
      title: 'Security & Audit',
      desc: 'Complete audit trails and bank-grade security for sensitive data.',
      color: '#795548'
    },
    {
      icon: '🔗',
      title: 'ERP Integration',
      desc: 'Seamless integration with SAP, Oracle, and other ERP systems.',
      color: '#607D8B'
    },
    {
      icon: '⚠️',
      title: 'Risk Assessment',
      desc: 'Automated risk scoring based on vendor profile and documentation.',
      color: '#E91E63'
    }
  ];

  return (
    <AnimatedSection className="sds_vo-features section">
      <div className="container">
        <div className="has-text-centered mb-6">
          <h2 className="title is-2 sds_vo-section-title">Comprehensive Vendor Management Features</h2>
          <p className="subtitle is-5">Everything procurement teams need</p>
        </div>

        <motion.div
          className="columns is-multiline"
          variants={staggerContainer}
          initial="hidden"
          whileInView="visible"
          viewport={{ once: true, amount: 0.2 }}
        >
          {features.map((feature, index) => (
            <motion.div key={index} className="column is-4" variants={scaleIn}>
              <motion.div
                className="sds_vo-feature-card"
                whileHover={{ y: -8, boxShadow: '0 15px 40px rgba(0,0,0,0.12)' }}
                transition={{ type: "spring", stiffness: 300 }}
              >
                <div className="sds_vo-feature-icon" style={{ background: feature.color }}>
                  {feature.icon}
                </div>
                <h3 className="title is-5 mt-4">{feature.title}</h3>
                <p className="has-text-grey">{feature.desc}</p>
              </motion.div>
            </motion.div>
          ))}
        </motion.div>

      </div>
    </AnimatedSection>
  );
};

// Vendor Categories Section
const VendorCategoriesSection: React.FC = () => {
  const categories = [
    {
      icon: '🏭',
      title: 'Manufacturers',
      color: '#1976D2',
      requirements: ['Business license', 'Manufacturing certification', 'Quality standards', 'Capacity proof', 'ISO certifications']
    },
    {
      icon: '🚚',
      title: 'Logistics Providers',
      color: '#388E3C',
      requirements: ['Transport license', 'Insurance certificate', 'Vehicle registration', 'GST certificate', 'PAN card']
    },
    {
      icon: '💼',
      title: 'Service Providers',
      color: '#7B1FA2',
      requirements: ['Service license', 'Professional certifications', 'Insurance docs', 'Bank details', 'References']
    },
    {
      icon: '🛒',
      title: 'Suppliers & Distributors',
      color: '#D32F2F',
      requirements: ['Trade license', 'Tax registration', 'Distribution agreement', 'Product certificates', 'Bank statements']
    },
    {
      icon: '💻',
      title: 'IT & Software Vendors',
      color: '#0097A7',
      requirements: ['Company registration', 'Certifications', 'Security compliance', 'NDA', 'Service agreements']
    },
    {
      icon: '🏗️',
      title: 'Contractors',
      color: '#F57C00',
      requirements: ['Contractor license', 'Safety certifications', 'Insurance coverage', 'Past project proof', 'Financial docs']
    }
  ];

  return (
    <AnimatedSection className="sds_vo-categories section sds_vo-bg-gradient">
      <div className="container">
        <div className="has-text-centered mb-6">
          <h2 className="title is-2 sds_vo-section-title has-text-white">Support for All Vendor Types</h2>
          <p className="subtitle is-5 has-text-white-ter">Customized onboarding for different vendor categories</p>
        </div>

        <motion.div
          className="columns is-multiline"
          variants={staggerContainer}
          initial="hidden"
          whileInView="visible"
          viewport={{ once: true, amount: 0.1 }}
        >
          {categories.map((category, index) => (
            <motion.div key={index} className="column is-4" variants={scaleIn}>
              <motion.div
                className="sds_vo-category-card"
                whileHover={{ y: -10 }}
                transition={{ type: "spring", stiffness: 300 }}
              >
                <div className="sds_vo-category-icon" style={{ background: `linear-gradient(135deg, ${category.color}, ${category.color}dd)` }}>
                  {category.icon}
                </div>
                <h3 className="title is-5 has-text-white has-text-centered mb-3">{category.title}</h3>
                <div className="sds_vo-requirements">
                  <p className="has-text-white-ter has-text-weight-bold mb-2 is-size-7">Required Documents:</p>
                  <ul className="sds_vo-req-list">
                    {category.requirements.map((req, i) => (
                      <li key={i}>
                        <span className="icon has-text-white is-small">
                          <i className="fas fa-check-circle"></i>
                        </span>
                        <span className="has-text-white-ter">{req}</span>
                      </li>
                    ))}
                  </ul>
                </div>
              </motion.div>
            </motion.div>
          ))}
        </motion.div>

      </div>
    </AnimatedSection>
  );
};

// Benefits Section
const BenefitsSection: React.FC = () => {
  const benefits = [
    {
      icon: '⚡',
      title: '90% Faster Onboarding',
      desc: 'Complete vendor registration in hours instead of weeks',
      stats: 'Hours vs Weeks'
    },
    {
      icon: '✅',
      title: '100% Compliance',
      desc: 'Automated compliance checks ensure regulatory adherence',
      stats: 'Zero compliance gaps'
    },
    {
      icon: '💰',
      title: 'Reduce Costs by 75%',
      desc: 'Eliminate manual verification and paperwork costs',
      stats: '₹1,00,000+ savings'
    },
    {
      icon: '🎯',
      title: 'Improved Vendor Quality',
      desc: 'AI-powered risk assessment identifies reliable vendors',
      stats: '40% better quality'
    },
    {
      icon: '📊',
      title: 'Complete Visibility',
      desc: 'Real-time dashboard shows vendor status and metrics',
      stats: 'Full transparency'
    },
    {
      icon: '🔒',
      title: 'Enhanced Security',
      desc: 'Bank-grade security protects sensitive vendor data',
      stats: '100% data security'
    }
  ];

  return (
    <AnimatedSection className="sds_vo-benefits section sds_vo-bg-light">
      <div className="container">
        <div className="has-text-centered mb-6">
          <h2 className="title is-2 sds_vo-section-title">Why Procurement Teams Choose Us</h2>
          <p className="subtitle is-5">Measurable improvements in vendor management</p>
        </div>

        <motion.div
          className="columns is-multiline"
          variants={staggerContainer}
          initial="hidden"
          whileInView="visible"
          viewport={{ once: true }}
        >
          {benefits.map((benefit, index) => (
            <motion.div key={index} className="column is-4" variants={scaleIn}>
              <motion.div
                className="sds_vo-benefit-card"
                whileHover={{ y: -10, boxShadow: '0 15px 40px rgba(0,0,0,0.12)' }}
                transition={{ type: "spring", stiffness: 300 }}
              >
                <div className="sds_vo-benefit-icon">{benefit.icon}</div>
                <h3 className="title is-5">{benefit.title}</h3>
                <p className="has-text-grey mb-3">{benefit.desc}</p>
                <div className="sds_vo-stats-badge">{benefit.stats}</div>
              </motion.div>
            </motion.div>
          ))}
        </motion.div>

      </div>
    </AnimatedSection>
  );
};

// Security & Compliance Section
const SecurityComplianceSection: React.FC = () => {
  const features = [
    {
      icon: '🔐',
      title: 'Data Encryption',
      desc: '256-bit SSL encryption for all vendor data and documents'
    },
    {
      icon: '✅',
      title: 'Regulatory Compliance',
      desc: 'GDPR, SOC 2, ISO 27001 certified platform'
    },
    {
      icon: '📜',
      title: 'Audit Trail',
      desc: 'Complete logs of all vendor interactions and changes'
    },
    {
      icon: '🛡️',
      title: 'Access Control',
      desc: 'Role-based permissions and multi-factor authentication'
    },
    {
      icon: '☁️',
      title: 'Secure Cloud',
      desc: 'Enterprise cloud infrastructure with automatic backups'
    },
    {
      icon: '⚖️',
      title: 'Legal Compliance',
      desc: 'E-signatures compliant with IT Act 2000 and e-SIGN'
    }
  ];

  return (
    <AnimatedSection className="sds_vo-security section">
      <div className="container">
        <div className="has-text-centered mb-6">
          <h2 className="title is-2 sds_vo-section-title">🔒 Enterprise Security & Compliance</h2>
          <p className="subtitle is-5">Protecting your vendor data with industry-leading security</p>
        </div>

        <motion.div
          className="columns is-multiline"
          variants={staggerContainer}
          initial="hidden"
          whileInView="visible"
          viewport={{ once: true }}
        >
          {features.map((feature, index) => (
            <motion.div key={index} className="column is-4" variants={fadeInUp}>
              <div className="sds_vo-security-card">
                <div className="sds_vo-security-icon">{feature.icon}</div>
                <h4 className="title is-5 has-text-centered">{feature.title}</h4>
                <p className="has-text-centered has-text-grey">{feature.desc}</p>
              </div>
            </motion.div>
          ))}
        </motion.div>

      </div>
    </AnimatedSection>
  );
};

// CTA Section
const CTASection: React.FC = () => {
  return (
    <AnimatedSection className="sds_vo-cta section">
      <div className="container">
        <div className="box sds_vo-cta-box">
          <div className="has-text-centered">
            <motion.div
              initial={{ scale: 0.9, opacity: 0 }}
              whileInView={{ scale: 1, opacity: 1 }}
              transition={{ duration: 0.5 }}
              viewport={{ once: true }}
            >
              <h2 className="title is-1 mb-5">Ready to Transform Vendor Management?</h2>
              <p className="subtitle is-4 mb-6">
                Join leading companies who have revolutionized their vendor onboarding
              </p>

              <div className="sds_vo-offer-badge mb-5">
                {/* <p className="is-size-3 has-text-weight-bold mb-2">🎁 Special Launch Offer</p> */}
                <p className="is-size-4">First 2 Vendor Onboardings FREE!</p>
                <p className="is-size-6 mt-3">No credit card • Fast setup • Full features • Cancel anytime</p>
              </div>

              <div className="buttons is-centered are-large">
                <motion.a
                  href={`tel:${InfoService.mobile_no_1}`}
                  className="button is-primary is-large sds_vo-cta-button"
                  whileHover={{ scale: 1.05, y: -3 }}
                  whileTap={{ scale: 0.95 }}
                >
                  <span className="icon">
                    <i className="fas fa-phone"></i>
                  </span>
                  <span>Call Now: {InfoService.mobile_no_1}</span>
                </motion.a>
                <motion.a
                    href={InfoService.loginLink}
                  className="button is-info is-large sds_vo-cta-button"
                  whileHover={{ scale: 1.05, y: -3 }}
                  whileTap={{ scale: 0.95 }}
                >
                  <span className="icon">
                    <i className="fas fa-envelope"></i>
                  </span>
                  <span>Get Free Trial</span>
                </motion.a>
              </div>

            </motion.div>
          </div>
        </div>
      </div>
    </AnimatedSection>
  );
};

// Main Component
const VendorOnboarding: React.FC = () => {
  return (
    <div className="sds_vo-wrapper">
      {/* SEO Meta Tags */}
      <div style={{ display: 'none' }}>
        <h1>HDT Solutions - Vendor Onboarding System with AI & Digital Signatures</h1>
        <meta name="description" content="Streamline vendor registration with automated verification, AI data extraction, and digital contracts. Onboard vendors 90% faster. Compliant and secure vendor management." />
        <meta name="keywords" content="vendor onboarding, supplier management, vendor registration, digital contracts, AI verification, procurement automation, HDT Solutions" />
      </div>

      <HeroSection />
      <HowItWorksSection />
      <KeyFeaturesSection />
      <VendorCategoriesSection />
      <BenefitsSection />
      <SecurityComplianceSection />
      <CTASection />

      <style>{`
        /* Base Styles */
        .sds_vo-wrapper {
          background: #ffffff;
          min-height: 100vh;
          font-family: 'Segoe UI', Tahoma, Geneva, Verdana, sans-serif;
        }

        /* Brand Colors */
        :root {
          --primary-orange: #FF6F00;
          --primary-deep-orange: #E65100;
          --dark-grey: #263238;
          --success-green: #4CAF50;
        }

        /* Hero Section */
        .sds_vo-hero {
          background: linear-gradient(135deg, #FFF3E0 0%, #FFE0B2 100%);
          padding: 4rem 1.5rem;
        }

        .sds_vo-hero-title {
          color: var(--dark-grey);
          font-weight: 800;
          line-height: 1.2;
        }

        .sds_vo-hero-subtitle {
          color: var(--primary-orange);
          font-weight: 600;
        }

        .sds_vo-hero-description {
          color: #555;
          line-height: 1.8;
        }

        .sds_vo-feature-badges {
          display: flex;
          flex-wrap: wrap;
          gap: 0.75rem;
          margin-top: 2rem;
        }

        .sds_vo-badge {
          background: linear-gradient(135deg, var(--primary-orange), var(--primary-deep-orange)) !important;
          color: white !important;
          font-weight: 600;
          padding: 0.75rem 1.5rem;
          border: none;
          box-shadow: 0 4px 15px rgba(255, 111, 0, 0.3);
        }

        .sds_vo-hero-visual {
          display: flex;
          justify-content: center;
          align-items: center;
          min-height: 400px;
        }

        .sds_vo-animation {
          position: relative;
          width: 100%;
          height: 400px;
          display: flex;
          justify-content: center;
          align-items: center;
        }

        .sds_vo-icon-large {
          font-size: 150px;
          filter: drop-shadow(0 10px 30px rgba(0, 0, 0, 0.1));
        }

        .sds_vo-floating-elements {
          position: absolute;
          width: 100%;
          height: 100%;
        }

        .sds_vo-floating-element {
          position: absolute;
          font-size: 50px;
          filter: drop-shadow(0 5px 15px rgba(0, 0, 0, 0.1));
        }

        .sds_vo-floating-element:nth-child(1) { top: 10%; left: 15%; }
        .sds_vo-floating-element:nth-child(2) { top: 60%; left: 10%; }
        .sds_vo-floating-element:nth-child(3) { top: 20%; right: 15%; }
        .sds_vo-floating-element:nth-child(4) { bottom: 20%; right: 20%; }

        /* Section Title */
        .sds_vo-section-title {
          color: var(--dark-grey);
          font-weight: 700;
          position: relative;
          display: inline-block;
        }

        .sds_vo-section-title::after {
          content: '';
          display: block;
          width: 80px;
          height: 4px;
          background: linear-gradient(90deg, var(--primary-orange), var(--primary-deep-orange));
          margin: 1rem auto 0;
          border-radius: 2px;
        }

        /* Background Gradients */
        .sds_vo-bg-gradient {
          background: linear-gradient(135deg, var(--primary-orange) 0%, var(--primary-deep-orange) 100%);
          color: white;
          padding: 4rem 1.5rem;
        }

        .sds_vo-bg-light {
          background: linear-gradient(135deg, #FFF3E0 0%, #FFE0B2 100%);
          padding: 4rem 1.5rem;
        }

        /* Process Section */
        .sds_vo-process {
          padding: 4rem 1.5rem;
        }

        .sds_vo-process-card {
          background: white;
          padding: 1.75rem 1.25rem;
          border-radius: 15px;
          text-align: center;
          height: 100%;
          box-shadow: 0 5px 20px rgba(0, 0, 0, 0.08);
          transition: all 0.3s ease;
          border-top: 4px solid var(--primary-orange);
        }

        .sds_vo-process-card:hover {
          box-shadow: 0 12px 35px rgba(255, 111, 0, 0.2);
        }

        .sds_vo-step-number {
          background: linear-gradient(135deg, var(--primary-orange), var(--primary-deep-orange));
          color: white;
          width: 40px;
          height: 40px;
          border-radius: 50%;
          display: flex;
          align-items: center;
          justify-content: center;
          font-weight: bold;
          margin: 0 auto 0.75rem;
          font-size: 1.25rem;
          box-shadow: 0 6px 20px rgba(255, 111, 0, 0.4);
        }

        .sds_vo-step-icon {
          font-size: 40px;
          margin-bottom: 0.75rem;
        }

        .sds_vo-feature-list {
          list-style: none;
          padding: 0;
          text-align: left;
        }

        .sds_vo-feature-list li {
          display: flex;
          align-items: center;
          margin-bottom: 0.35rem;
          color: #555;
          font-size: 0.75rem;
        }

        .sds_vo-feature-list li .icon {
          margin-right: 0.4rem;
        }

        /* Features Section */
        .sds_vo-features {
          padding: 4rem 1.5rem;
        }

        .sds_vo-feature-card {
          background: white;
          border-radius: 20px;
          padding: 2.5rem 2rem;
          height: 100%;
          box-shadow: 0 5px 20px rgba(0, 0, 0, 0.08);
          transition: all 0.3s ease;
          text-align: center;
        }

        .sds_vo-feature-icon {
          width: 80px;
          height: 80px;
          border-radius: 20px;
          display: flex;
          align-items: center;
          justify-content: center;
          font-size: 40px;
          margin: 0 auto;
          box-shadow: 0 8px 20px rgba(0, 0, 0, 0.1);
        }

        /* Category Section */
        .sds_vo-categories {
          padding: 4rem 1.5rem;
        }

        .sds_vo-category-card {
          background: rgba(255, 255, 255, 0.1);
          border-radius: 20px;
          padding: 2rem 1.5rem;
          height: 100%;
          backdrop-filter: blur(10px);
          border: 2px solid rgba(255, 255, 255, 0.2);
          transition: all 0.3s ease;
        }

        .sds_vo-category-card:hover {
          background: rgba(255, 255, 255, 0.15);
          border-color: rgba(255, 255, 255, 0.4);
        }

        .sds_vo-category-icon {
          width: 70px;
          height: 70px;
          border-radius: 15px;
          display: flex;
          align-items: center;
          justify-content: center;
          font-size: 35px;
          margin: 0 auto 1rem;
          box-shadow: 0 5px 15px rgba(0, 0, 0, 0.15);
        }

        .sds_vo-requirements {
          margin-top: 1rem;
        }

        .sds_vo-req-list {
          list-style: none;
          padding: 0;
        }

        .sds_vo-req-list li {
          display: flex;
          align-items: center;
          margin-bottom: 0.4rem;
          font-size: 0.85rem;
        }

        .sds_vo-req-list li .icon {
          margin-right: 0.5rem;
        }

        /* Benefits Section */
        .sds_vo-benefits {
          padding: 4rem 1.5rem;
        }

        .sds_vo-benefit-card {
          background: white;
          border-radius: 20px;
          padding: 2.5rem 2rem;
          height: 100%;
          box-shadow: 0 5px 20px rgba(0, 0, 0, 0.08);
          text-align: center;
          transition: all 0.3s ease;
        }

        .sds_vo-benefit-icon {
          font-size: 60px;
          margin-bottom: 1rem;
        }

        .sds_vo-stats-badge {
          background: linear-gradient(135deg, var(--primary-orange), var(--primary-deep-orange));
          color: white;
          padding: 0.5rem 1rem;
          border-radius: 20px;
          font-weight: bold;
          display: inline-block;
          margin-top: 1rem;
        }

        /* Security Section */
        .sds_vo-security {
          padding: 4rem 1.5rem;
        }

        .sds_vo-security-card {
          background: white;
          padding: 2rem;
          border-radius: 15px;
          text-align: center;
          box-shadow: 0 5px 15px rgba(0, 0, 0, 0.08);
          height: 100%;
        }

        .sds_vo-security-icon {
          font-size: 50px;
          margin-bottom: 1rem;
        }

        /* CTA Section */
        .sds_vo-cta {
          padding: 4rem 1.5rem;
          background: linear-gradient(135deg, #FFF3E0 0%, #FFE0B2 100%);
        }

        .sds_vo-cta-box {
          background: white;
          border-radius: 30px;
          padding: 4rem 2rem;
          box-shadow: 0 15px 50px rgba(0, 0, 0, 0.15);
        }

        .sds_vo-offer-badge {
          background: linear-gradient(135deg, #FFF9C4, #FFECB3);
          border: 3px solid #FFA726;
          border-radius: 20px;
          padding: 2rem;
          display: inline-block;
        }

        .sds_vo-cta-button {
          font-weight: 700;
          text-transform: uppercase;
          letter-spacing: 1px;
          padding: 1.5rem 2.5rem !important;
          border-radius: 50px !important;
          box-shadow: 0 8px 25px rgba(0, 0, 0, 0.2);
          transition: all 0.3s ease;
        }

        .sds_vo-cta-button:hover {
          box-shadow: 0 12px 35px rgba(0, 0, 0, 0.3);
        }

        /* Video Placeholder */
        .sds_vo-video-container {
          margin: 3rem 0;
          display: flex;
          justify-content: center;
        }

        .sds_vo-video-placeholder {
          background: linear-gradient(135deg, rgba(255, 111, 0, 0.1), rgba(230, 81, 0, 0.1));
          border: 3px dashed var(--primary-orange);
          border-radius: 20px;
          padding: 3rem 2rem;
          text-align: center;
          width: 100%;
          max-width: 800px;
          cursor: pointer;
          transition: all 0.3s ease;
        }

        .sds_vo-video-placeholder:hover {
          background: linear-gradient(135deg, rgba(255, 111, 0, 0.15), rgba(230, 81, 0, 0.15));
          border-color: var(--primary-deep-orange);
          box-shadow: 0 10px 30px rgba(0, 0, 0, 0.1);
        }

        .sds_vo-video-light {
          background: rgba(255, 255, 255, 0.9);
          border-color: rgba(255, 255, 255, 0.5);
        }

        .sds_vo-video-light:hover {
          background: white;
          border-color: white;
        }

        .sds_vo-video-placeholder .icon {
          color: var(--primary-orange);
          transition: all 0.3s ease;
        }

        .sds_vo-video-placeholder:hover .icon {
          color: var(--primary-deep-orange);
          transform: scale(1.1);
        }

        /* Responsive Design */
        @media screen and (max-width: 768px) {
          .sds_vo-hero {
            padding: 2rem 1rem;
          }

          .sds_vo-hero-title {
            font-size: 2rem;
          }

          .sds_vo-icon-large {
            font-size: 100px;
          }

          .sds_vo-floating-element {
            font-size: 35px;
          }

          .sds_vo-section-title {
            font-size: 1.75rem;
          }

          .sds_vo-video-placeholder {
            padding: 2rem 1rem;
          }

          .sds_vo-cta-box {
            padding: 2rem 1rem;
          }

          .sds_vo-offer-badge {
            padding: 1.5rem;
          }

          .sds_vo-cta-button {
            padding: 1rem 1.5rem !important;
            font-size: 0.9rem;
          }
        }
      `}</style>
    </div>
  );
};

export default VendorOnboarding;
